import React, { useMemo, useState } from "react";
import CheckBox from "../../../../../components/UI/CheckBox";
import { Button } from "../../../../../components/UI/Button";
import TextInput from "../../../../../components/UI/TextInput";
import Modal from "../../../../../components/UI/Modal";
import type { ProductEntity } from "../../../../../../domain/types/product";
import type { ProductLightDto } from "../../../../../../domain/types/shop";

interface SectionProductPickerModalProps {
  setIsModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  isModalVisible: boolean;
  sectionTitle: string;
  /** 고를 수 있는 전체 상품 */
  productsData: ProductEntity[] | null;
  /** 현재 섹션에 담긴 상품 (배열 순서가 곧 display_order) */
  selectedProducts: ProductLightDto[];
  onChange: (products: ProductLightDto[]) => void;
}

/**
 * 베스트셀러 상품 선택 모달과 같은 구조지만 개수 제한이 없다.
 * 베스트셀러의 MAX_BEST_SELLER_COUNT(5) 는 여기 적용되지 않는다.
 */
export const SectionProductPickerModal = ({
  setIsModalVisible,
  isModalVisible,
  sectionTitle,
  productsData,
  selectedProducts,
  onChange,
}: SectionProductPickerModalProps) => {
  const [searchKeyword, setSearchKeyword] = useState("");

  const selectedIds = useMemo(
    () => new Set(selectedProducts.map((product) => product.id)),
    [selectedProducts],
  );

  const visibleProducts = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    if (!keyword) return productsData ?? [];

    return (productsData ?? []).filter((product) =>
      product.name.toLowerCase().includes(keyword),
    );
  }, [productsData, searchKeyword]);

  const toProductLight = (product: ProductEntity): ProductLightDto => ({
    id: product.id,
    thumbnail: product.thumbnail ?? null,
    name: product.name,
    description: product.description ?? null,
    price: product.priceTiers?.[0]?.price ?? 0,
  });

  const toggleProduct = (product: ProductEntity) => {
    if (selectedIds.has(product.id)) {
      onChange(selectedProducts.filter((item) => item.id !== product.id));
      return;
    }

    // 새로 고른 상품은 맨 뒤에 붙는다 — 배열 순서가 곧 노출 순서다
    onChange([...selectedProducts, toProductLight(product)]);
  };

  if (!isModalVisible) {
    return null;
  }

  return (
    <Modal setIsModalVisible={setIsModalVisible} title="">
      <div className="w-[calc(100vw-20vw)] max-w-[31.875rem]">
        <div className="flex flex-col items-start / mb-[1.0625rem]">
          <h2 className="mb-[0.75rem]">{sectionTitle} 상품 선택</h2>
          <div className="text-[0.875rem] font-[500]">
            {selectedProducts.length}개 선택됨
          </div>
        </div>
        <div className="relative py-[1rem]">
          <div
            className="absolute top-0 bottom-0 left-0 right-0
              / mx-[-19px] / bg-[#F9FAFB] / outline outline-1 outline-[#E5E7EB]"
          ></div>
          <TextInput
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            className="w-full"
            placeholder="상품 검색..."
          />
        </div>
        <div
          className="h-[308px] overflow-y-scroll / flex flex-col
            / gap-[1.1rem] mt-[1.390625rem] mb-[1.390625rem]"
        >
          {visibleProducts.map((item) => {
            const isSelected = selectedIds.has(item.id);

            return (
              <React.Fragment key={item.id}>
                <div className="flex flex- justify-between items-center gap-3">
                  <div className="flex flex-row gap-[1rem] items-center">
                    <button onClick={() => toggleProduct(item)}>
                      <CheckBox
                        className="min-w-[1rem]"
                        isEnabled={isSelected}
                      />
                    </button>

                    <div className="min-w-[3.75rem] h-min aspect-[1/1] / bg-[#AEAEAE] / rounded-[0.625rem] overflow-hidden">
                      {item.thumbnail ? (
                        <img
                          className="w-full h-full object-cover"
                          src={item.thumbnail}
                          alt="썸네일 이미지."
                        />
                      ) : null}
                    </div>
                    <div className="flex flex-col items-start justify-center">
                      <h3 className="text-[0.9375rem]">{item.name}</h3>
                      <div className="text-[#AEAEAE] text-[12px] text-left flex flex-col">
                        <div className="mb-1">{item.description}</div>
                        <span className="text-[#4285F4]">
                          ₩{item.priceTiers?.[0]?.price ?? "-"}
                        </span>
                      </div>
                    </div>
                  </div>
                  {isSelected ? (
                    <div
                      className="hidden min-w-[70px] justify-center md:flex text-[0.75rem] text-white / bg-[#4285F4]
                        / px-[0.8rem] py-[0.6rem] h-min / rounded-full
                        / items-center gap-[0.375rem]"
                    >
                      <div
                        className="bg-white / w-[0.375rem] h-[0.375rem]
                          / rounded-full"
                      />
                      선택됨
                    </div>
                  ) : null}
                </div>
                <hr />
              </React.Fragment>
            );
          })}

          {visibleProducts.length === 0 ? (
            <div className="py-[1rem] text-center text-[#AEAEAE]">
              검색 결과가 없습니다.
            </div>
          ) : null}
        </div>
        <div className="relative">
          <div
            className="absolute top-0 bottom-0 left-0 right-0
              / mx-[-19px]  / bg-[#F9FAFB] outline outline-1 outline-[#E5E7EB]
              / rounded-b-[12px]"
          ></div>
          <div
            className="relative z-50 / flex flex-row justify-between items-center
              / py-[1rem] mb-[-20px]"
          >
            <div className="text-[0.8125rem] / font-normal">
              {selectedProducts.length}개 선택됨 · 개수 제한 없음
            </div>
            <div>
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                }}
                className="w-[4.014375rem] h-[2.5625rem]"
              >
                완료
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
